import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { loginNextTarget } from './loginNext';
import { useAuth } from './useAuth';

interface Props {
    children: ReactNode;
}

/**
 * The auth guard.
 *
 *   - Not signed in → redirect to /login?next=<current path>.
 *   - Signed in but no active org → redirect to /login (the login page also
 *     hosts org selection / first-run org creation).
 */
export const AuthGate = ({ children }: Props): ReactNode => {
    const { isSignedIn, orgId } = useAuth();
    const loc = useLocation();

    if (!isSignedIn || !orgId) {
        // loginNextTarget unwraps /setup-server to its own ?next — the pairing
        // placeholder is a derived screen, not a return destination (see
        // auth/loginNext.ts for the sign-out loop this prevents).
        const nextTarget = encodeURIComponent(loginNextTarget(loc.pathname, loc.search));
        return (
            <Navigate
                to={`/login?next=${nextTarget}`}
                replace
            />
        );
    }
    return children;
};
